import { useSearchParams } from "react-router-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import type { RecordObject } from "@/profitability/records/objects";
import type {
  ProfitabilityDocument,
  ReportOutput,
} from "@/profitability/types";
import { citationFromParams } from "./citation";
import { DocumentDetails } from "./document-details";
import type { DocumentEntry } from "./document-entries";
import { DocumentHeader } from "./document-header";
import { DocumentOriginal } from "./document-original";
import { DocumentPreview } from "./document-preview";
import { DocumentUsedFor } from "./document-used-for";
import type { DocumentsRun } from "./documents-tab";
import type { DocumentLinks } from "./links";
import { useDocumentDetail } from "./query";
import { RefusedWorkbook } from "./refused-workbook";
import {
  type DocumentView,
  documentView,
  documentViews,
  originalKind,
} from "./views";

/**
 * The selected document: its name and what it is, what can be done with it,
 * and the ways to look at it: the file as uploaded (Original, for a file a
 * viewer draws; a CSV as a spreadsheet), what was read from it (Preview, but
 * not for a spreadsheet, whose Original is its rows), what in the report it
 * fed, and its details. The view is in the URL (`?view=`), with the place a
 * citation points to (`?page=&box=&sheet=&row=&col=&find=`, ./citation).
 */
export const DocumentPane = ({
  businessId,
  run,
  entry,
  document,
  output,
  objects,
  links,
  canUpload,
  onAsk,
}: {
  businessId: string;
  run: DocumentsRun;
  entry: DocumentEntry;
  document: ProfitabilityDocument;
  output?: ReportOutput;
  objects: RecordObject[];
  links: DocumentLinks;
  canUpload: boolean;
  onAsk?: (question: string) => void;
}) => {
  const [params, setParams] = useSearchParams();
  const kind = originalKind(document);
  const view = documentView(params.get("view"), kind);
  const citation = citationFromParams(params);
  const setView = (next: DocumentView) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        updated.set("view", next);
        return updated;
      },
      { replace: true },
    );
  // Who uploaded it and the periods that read it; the run's own copy of
  // the document carries the rest, so the pane draws before this arrives.
  const detail = useDocumentDetail(businessId, document.id);
  const shown = { ...document, ...detail.data };
  return (
    <Stack
      render={<section aria-label={document.file_name} />}
      gap={4}
      align="stretch"
      className="min-w-0 flex-1 lg:min-h-0"
    >
      <DocumentHeader
        businessId={businessId}
        run={run}
        entry={entry}
        document={shown}
        replaceHref={
          canUpload && entry.role === "input"
            ? links.upload(entry.slot?.key ?? null)
            : null
        }
        onAsk={onAsk}
      />
      <ToggleGroup
        aria-label="Show"
        value={[view]}
        onValueChange={(next) => next[0] && setView(next[0] as DocumentView)}
        variant="outline"
        spacing={0}
        size="sm"
      >
        {documentViews(kind).map((item) => (
          <ToggleGroupItem key={item.value} value={item.value}>
            {item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {view === "original" && kind && (
        <DocumentOriginal
          businessId={businessId}
          document={document}
          kind={kind}
          citation={citation}
          fallback={(reason) => (
            <RefusedWorkbook reason={reason}>
              <DocumentPreview businessId={businessId} document={document} />
            </RefusedWorkbook>
          )}
        />
      )}
      {view === "preview" && (
        <DocumentPreview
          businessId={businessId}
          document={document}
          citation={citation}
        />
      )}
      {view === "used" && (
        <DocumentUsedFor
          run={run}
          entry={entry}
          documentId={document.id}
          output={output}
          objects={objects}
          runs={detail.data?.runs}
          runsLoading={detail.isPending}
          links={links}
        />
      )}
      {view === "details" && (
        <DocumentDetails
          businessId={businessId}
          entry={entry}
          document={shown}
          matches={(run.matched_columns ?? []).filter(
            (match) => match.document_id === document.id,
          )}
        />
      )}
    </Stack>
  );
};
